import type { Audience } from "../types.js";

/**
 * Auth-specific error codes. Narrow string-literal union for ts-pattern exhaustive matching.
 *
 * These mirror the backend error envelope (`errorCode` field in RFC 7807 responses).
 * When backend Phase 2 ships openapi.json with real codes, this list regenerates via
 * `pnpm gen:api` — any new codes surface as a compile error at every `.with({ errorCode }, ...)`
 * site in login / reset / OTP handlers.
 */
export type AuthErrorCode =
  | "AUTH_BAD_CREDENTIALS"
  // 403 on login / OTP / TOTP / refresh — the account holds no admin-capable
  // role (be-788 / CC-431). Distinct from bad credentials: the password was
  // right, this surface just isn't theirs. On refresh it means a mid-session
  // demotion. NEVER render the generic bad-credentials copy for it.
  | "AUTH_ADMIN_AUDIENCE_NOT_PERMITTED"
  // 403 on login / OTP / TOTP: the password was right, but the account holds
  // no access to Link Accounting. NEVER the bad-credentials copy.
  | "AUTH_ACCOUNTING_AUDIENCE_NOT_PERMITTED"
  // 403 on an HR sign-in: the account opens Link Accounting only.
  | "PRODUCT_LOCKED"
  | "TENANT_NOT_FOUND"
  | "TENANT_SUSPENDED"
  // 403 / 404 at sign-in or refresh: the company's workspace data was purged,
  // or it has no HR subscription. Terminal for this workspace, never a retry.
  | "SUBSCRIPTION_PURGED"
  | "SUBSCRIPTION_NOT_FOUND"
  | "ACCOUNT_LOCKED"
  | "OTP_REQUIRED"
  | "OTP_INVALID"
  | "OTP_EXPIRED"
  | "REFRESH_EXPIRED"
  | "REFRESH_FAMILY_REVOKED"
  | "RESET_TOKEN_EXPIRED"
  | "RESET_TOKEN_INVALID"
  // 401 at sign-in / password change: the temporary password HR issued is
  // older than its validity window. Only HR can issue a new one.
  | "AUTH_TEMP_PASSWORD_EXPIRED"
  // 403 at sign-in: the company requires emailed codes and the account has
  // no email to send one to.
  | "AUTH_SECOND_FACTOR_UNAVAILABLE"
  // 403 on any call but the password change, refresh and logout while the
  // session holds a temporary password.
  | "AUTH_PASSWORD_CHANGE_REQUIRED";

export const AUTH_ERROR_CODES: readonly AuthErrorCode[] = [
  "AUTH_BAD_CREDENTIALS",
  "AUTH_ADMIN_AUDIENCE_NOT_PERMITTED",
  "AUTH_ACCOUNTING_AUDIENCE_NOT_PERMITTED",
  "PRODUCT_LOCKED",
  "TENANT_NOT_FOUND",
  "TENANT_SUSPENDED",
  "SUBSCRIPTION_PURGED",
  "SUBSCRIPTION_NOT_FOUND",
  "ACCOUNT_LOCKED",
  "OTP_REQUIRED",
  "OTP_INVALID",
  "OTP_EXPIRED",
  "REFRESH_EXPIRED",
  "REFRESH_FAMILY_REVOKED",
  "RESET_TOKEN_EXPIRED",
  "RESET_TOKEN_INVALID",
  "AUTH_TEMP_PASSWORD_EXPIRED",
  "AUTH_SECOND_FACTOR_UNAVAILABLE",
  "AUTH_PASSWORD_CHANGE_REQUIRED",
] as const;

/**
 * The `auth` copy a sign-in step (password, emailed code, authenticator) shows
 * when the account can't open Link Accounting, or null for any other code.
 */
export function accountingRefusalKey(errorCode: string): string | null {
  return errorCode === "AUTH_ACCOUNTING_AUDIENCE_NOT_PERMITTED"
    ? "login.error.accounting_not_permitted"
    : null;
}

/**
 * An HR sign-in (admin or mobile) refused because the account opens Link
 * Accounting only. Inside Link Accounting the same code means something else.
 */
export function isAccountingOnlyRefusal(errorCode: string, audience: Audience): boolean {
  return errorCode === "PRODUCT_LOCKED" && (audience === "admin" || audience === "mobile");
}
